import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut, getCurrentUser } from '../common/auth';
import { useApi } from '../hooks/useApi';
import './Header.css';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { callApi } = useApi();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isLanguageOpen, setIsLanguageOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [userName, setUserName] = useState('');
    const [currentLanguage, setCurrentLanguage] = useState('ES');
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const dropdownRef = useRef(null);
    const notificationRef = useRef(null);
    const languageRef = useRef(null);
    const mobileMenuRef = useRef(null);

    const NOTIF_REFRESH_INTERVAL = Number.isInteger(
        parseInt(process.env.REACT_APP_NOTIF_REFRESH_MS, 10)
    )
        ? parseInt(process.env.REACT_APP_NOTIF_REFRESH_MS, 10)
        : 600000; // 10 minutos

    const LIST_LAST_NOTIFICATIONS_QUERY = `
        query ListNotification {
            listNotification(limit: 3,read: "0", orderBy: "time", orderDirection: "desc") {
                totalCount
                items {
                    id
                    title
                    message
                    type
                    read
                    time
                }
            }
        }
    `;

    const LIST_UNREAD_COUNT_QUERY = `
        query ListNotification {
            listNotification(read: "0") {
                totalCount
            }
        }
    `;

    const UPDATE_NOTIFICATION_READ_MUTATION = `
        mutation UpdateNotificationRead($id: ID!) {
            updateNotificationRead(id: $id, read: true) {
                id
                read
                updated_at
            }
        }
    `;

    const fetchNotifications = async () => {
        try {
            const last = await callApi(LIST_LAST_NOTIFICATIONS_QUERY);
            const total = await callApi(LIST_UNREAD_COUNT_QUERY);
            setNotifications(last.listNotification.items);
            setUnreadCount(total.listNotification.totalCount);
        } catch (err) {
            console.error("Error fetching notifications", err);
        }
    };

    const intervalRef = useRef(null);

    useEffect(() => {
        fetchNotifications();

        if (!intervalRef.current) {
            intervalRef.current = setInterval(fetchNotifications, NOTIF_REFRESH_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const user = getCurrentUser();
        if (user) {
            user.getSession((err, session) => {
                if (!err && session) {
                    const name = session.getIdToken().payload.name || session.getIdToken().payload.email || 'Usuario';
                    setUserName(name);
                }
            });
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsDropdownOpen(false);
            if (notificationRef.current && !notificationRef.current.contains(event.target)) setIsNotificationOpen(false);
            if (languageRef.current && !languageRef.current.contains(event.target)) setIsLanguageOpen(false);
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) setIsMobileMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => setIsMobileMenuOpen(false), [location]);

    const handleNotificationItemClick = async (notification) => {
        if (!notification.read) {
            try {
                await callApi(UPDATE_NOTIFICATION_READ_MUTATION, { id: notification.id });
                fetchNotifications();
            } catch (err) {
                console.error("Error marking notification as read:", err);
            }
        }
    };

    const handleNotificationClick = () => setIsNotificationOpen(prev => !prev);

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'invoice': return '📄';
            case 'receipt': return '🧾';
            case 'warning': return '⚠️';
            case 'error': return '❌';
            case 'success': return '✅';
            default: return '📌';
        }
    };

    const handleLanguageChange = (lang) => {
        setCurrentLanguage(lang);
        setIsLanguageOpen(false);
    };

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    const isActive = (path) => location.pathname === path;
    const getUserInitials = () => userName ? userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'US';

    return (
        <header className="header">
            <div className="header-container">
                {/* Mobile Menu Button */}
                <button
                    className="mobile-menu-button"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Menu"
                >
                    {isMobileMenuOpen ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <line x1="3" y1="12" x2="21" y2="12" />
                            <line x1="3" y1="18" x2="21" y2="18" />
                        </svg>
                    )}
                </button>

                {/* Logo */}
                <div className="logo-section">
                    <div
                        className="logo"
                        onClick={() => navigate('/home')}
                    >
                        <span className="logo-text">TagNize</span>
                    </div>
                </div>

                {/* Desktop Navigation */}
                <nav className="nav-section desktop-nav">
                    <a
                        href="/home"
                        className={`nav-link ${isActive('/home') ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/home');
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        </svg>
                        Home
                    </a>
                    <a
                        href="/filemanager"
                        className={`nav-link ${isActive('/filemanager') ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/filemanager');
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        Buscar
                    </a>
                    <a
                        href="/upload"
                        className={`nav-link ${isActive('/upload') ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/upload');
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Subir Archivo
                    </a>
                    <a
                        href="/copilot"
                        className={`nav-link ${isActive('/copilot') || isActive('/Copilot') ? 'active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/Copilot');
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
                        </svg>
                        Copiloto
                    </a>
                </nav>

                {/* Mobile Navigation Menu */}
                <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} ref={mobileMenuRef}>
                    <nav className="mobile-nav">
                        <a
                            href="/home"
                            className={`mobile-nav-link ${isActive('/home') ? 'active' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/home');
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            </svg>
                            Home
                        </a>
                        <a
                            href="/filemanager"
                            className={`mobile-nav-link ${isActive('/filemanager') ? 'active' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/filemanager');
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                            </svg>
                            Buscar
                        </a>

                        <a
                            href="/upload"
                            className={`mobile-nav-link ${isActive('/upload') ? 'active' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/upload');
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            Subir Archivo
                        </a>
                        <a
                            href="/copilot"
                            className={`mobile-nav-link ${isActive('/copilot') || isActive('/Copilot') ? 'active' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/Copilot');
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
                            </svg>
                            Copiloto
                        </a>
                    </nav>
                </div>

                {/* Right Section */}
                <div className="right-section">
                    {/* Notifications */}
                    <div className="notification-container" ref={notificationRef}>
                        <button
                            className="icon-button notification-button"
                            onClick={handleNotificationClick}
                            aria-label="Notifications"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="notification-badge">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </button>


                        {isNotificationOpen && (
                            <div className="notification-dropdown">
                                <div className="notification-header">
                                    <h3>Notificaciones({unreadCount})</h3>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                    </svg>
                                    {unreadCount > 0 && (
                                        <span className="notification-badge">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    )}


                                </div>

                                <div className="notification-list">
                                    {notifications.map(n => (
                                        <div
                                            key={n.id}
                                            className={`notification-item ${!n.read ? 'unread' : ''}`}
                                            onClick={() => handleNotificationItemClick(n)}
                                        >
                                            <div className="notification-icon">{getNotificationIcon(n.type)}</div>
                                            <div className="notification-content">
                                                <div className="notification-title">{n.title}</div>
                                                <div className="notification-message">{n.message}</div>
                                                <div className="notification-time">{new Date(n.time).toLocaleString()}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="notification-footer">
                                    <button className="notification-footer-btn" onClick={() => navigate('/notifications')}>
                                        Ver todas las notificaciones
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Language */}
                    <div className="language-container" ref={languageRef}>
                        <button
                            className="icon-button language-button"
                            onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                            aria-label="Language"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                            </svg>
                        </button>
                        {isLanguageOpen && (
                            <div className="language-dropdown">
                                <button className={`language-option ${currentLanguage === 'ES' ? 'active' : ''}`} onClick={() => handleLanguageChange('ES')}>🇪🇸 Español</button>
                                <button className={`language-option ${currentLanguage === 'EN' ? 'active' : ''}`} onClick={() => handleLanguageChange('EN')}>🇺🇸 English</button>
                                <button className={`language-option ${currentLanguage === 'PT' ? 'active' : ''}`} onClick={() => handleLanguageChange('PT')}>🇧🇷 Português</button>
                            </div>
                        )}
                    </div>

                    {/* User */}
                    <div className="user-section" ref={dropdownRef}>
                        <button className="user-button" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                            <div className="user-avatar">{getUserInitials()}</div>
                        </button>
                        {isDropdownOpen && (
                            <div className="user-dropdown">
                                <div className="user-dropdown-header">
                                    <div className="user-avatar-large">{getUserInitials()}</div>
                                    <div className="user-dropdown-info">
                                        <span className="user-dropdown-name">{userName}</span>
                                        <span className="user-dropdown-email">{getCurrentUser()?.getUsername()}</span>
                                    </div>
                                </div>
                                <div className="dropdown-divider"></div>
                                <button className="dropdown-item" onClick={() => navigate('/profile')}>Mi Perfil</button>
                                <button className="dropdown-item" onClick={() => navigate('/settings')}>Configuración</button>
                                <div className="dropdown-divider"></div>
                                <button className="dropdown-item logout" onClick={handleLogout}>Cerrar Sesión</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
